import { Member, mapSupabaseToMember, mapMemberToSupabase } from '@/types/member';
import { supabase } from '@/integrations/supabase/client';
import { CRMEnrichmentService } from './crmEnrichmentService';

// ============= UNIFIED MEMBER SERVICE =============
// This service handles all member operations for both:
// - Public member directory (read-only)
// - Admin member management (full CRUD)

// ============= READ OPERATIONS (Public & Admin) =============

export const fetchMembers = async (): Promise<Member[]> => {
  try {
    const { data, error } = await supabase
      .from('club_members')
      .select('*')
      .not('user_id', 'is', null)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    const mappedMembers = data?.map(member => mapSupabaseToMember(member)) || [];
    return CRMEnrichmentService.enrichMembers(mappedMembers);
  } catch (error) {
    console.error('Error fetching members:', error);
    return [];
  }
};

// Optimized initial load - uses RPC for photo-priority sorting at DB level
export const fetchPublicMembers = async (limit = 12, offset = 0): Promise<Member[]> => {
  try {
    // Use RPC for proper ORDER BY CASE (photo first, then date)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_public_members', { p_limit: limit, p_offset: offset });

    if (rpcError) {
      console.warn('RPC get_public_members failed, falling back to direct query:', rpcError);
      // Fallback to direct query if RPC not available
      const { data, error } = await supabase
        .from('club_members')
        .select(`
          id, firstname, lastname, name, email, company, role, image, city, country, user_id, created_at,
          bio, languages, is_team_member, needs, offers
        `)
        .in('status', ['active', 'actif'])
        .eq('is_visible_in_directory', true)
        .not('user_id', 'is', null)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (error) throw error;
      return data?.map(member => mapSupabaseToMember(member)) || [];
    }

    return rpcData?.map((member: any) => mapSupabaseToMember(member)) || [];
  } catch (error) {
    console.error('Error fetching public members:', error);
    return [];
  }
};

/**
 * Phase 2: Lazy loading - Fetch enrichment data for visible members
 * Charges uniquement les champs lourds (skills, needs, interests, tags) pour les membres visibles
 */
export const fetchMemberEnrichment = async (memberIds: string[]): Promise<Record<string, Partial<Member>>> => {
  try {
    if (memberIds.length === 0) return {};
    
    const { data, error } = await supabase
      .from('club_members')
      .select('id, skills, needs, interests, tags')
      .in('id', memberIds);
    
    if (error) throw error;
    
    // Transform to a map for easy lookup
    const enrichmentMap: Record<string, Partial<Member>> = {};
    data?.forEach(item => {
      enrichmentMap[item.id] = {
        skills: item.skills || [],
        needs: item.needs || [],
        interests: item.interests || [],
        tags: item.tags || []
      };
    });
    
    return enrichmentMap;
  } catch (error) {
    console.error('Error fetching member enrichment:', error);
    return {};
  }
};

// Enhanced query for full member details (on-demand)
export const fetchMemberDetails = async (memberId: string): Promise<Member | null> => {
  try {
    const { data, error } = await supabase
      .from('club_members')
      .select('*')
      .eq('id', memberId)
      .eq('is_visible_in_directory', true)
      .maybeSingle();
    
    if (error) throw error;
    return data ? mapSupabaseToMember(data) : null;
  } catch (error) {
    console.error('Error fetching member details:', error);
    return null;
  }
};

export const fetchTotalMembersCount = async (): Promise<{ total: number; active: number }> => {
  try {
    // Count total visible members
    const { count: totalCount, error: totalError } = await supabase
      .from('club_members')
      .select('*', { count: 'exact', head: true })
      .eq('is_visible_in_directory', true)
      .not('user_id', 'is', null);
    
    if (totalError) throw totalError;

    // Count active members
    const { count: activeCount, error: activeError } = await supabase
      .from('club_members')
      .select('*', { count: 'exact', head: true })
      .in('status', ['active', 'actif'])
      .eq('is_visible_in_directory', true)
      .not('user_id', 'is', null);
    
    if (activeError) throw activeError;

    return {
      total: totalCount || 0,
      active: activeCount || 0
    };
  } catch (error) {
    console.error('Error fetching total members count:', error);
    return { total: 0, active: 0 };
  }
};

// ============= SERVER-SIDE SEARCH =============

export const searchMembersServer = async (term: string, limit = 50): Promise<Member[]> => {
  try {
    const searchPattern = `%${term}%`;
    
    const { data, error } = await supabase
      .from('club_members')
      .select('*')
      .in('status', ['active', 'actif'])
      .eq('is_visible_in_directory', true)
      .not('user_id', 'is', null)
      .or(`name.ilike.${searchPattern},firstname.ilike.${searchPattern},lastname.ilike.${searchPattern},company.ilike.${searchPattern},role.ilike.${searchPattern},city.ilike.${searchPattern},country.ilike.${searchPattern}`)
      .limit(limit);
    
    if (error) throw error;
    return data?.map(member => mapSupabaseToMember(member)) || [];
  } catch (error) {
    console.error('Error searching members server-side:', error);
    return [];
  }
};

// ============= ADMIN OPERATIONS (CRUD) =============

// Fonction pour créer un utilisateur pour un membre existant
export const createUserForExistingMember = async (email: string, password: string): Promise<void> => {
  try {
    // Créer l'utilisateur avec l'API Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: email,
      password: password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: {
          firstname: email.split('@')[0],
          lastname: 'User'
        }
      }
    });

    if (authError) {
      console.error('Error creating user:', authError);
      throw new Error(`Erreur lors de la création du compte: ${authError.message}`);
    }

    if (!authData.user) {
      throw new Error('Impossible de créer le compte utilisateur');
    }

    // Mettre à jour le membre pour pointer vers le nouvel utilisateur
    const { error: updateError } = await supabase
      .from('club_members')
      .update({ user_id: authData.user.id })
      .eq('email', email);

    if (updateError) {
      console.error('Error updating member:', updateError);
      throw new Error(`Erreur lors de la mise à jour du membre: ${updateError.message}`);
    }

    console.log('User created and linked to member successfully');
  } catch (error) {
    console.error('Error in createUserForExistingMember:', error);
    throw error;
  }
};

// ============= DEPRECATED - USE EDGE FUNCTION INSTEAD =============
// This function is deprecated to prevent auth session conflicts
// Use the create-user-admin Edge Function via CreateUserWithProfileDialog component instead

// ============= DEPRECATED FUNCTIONS (DO NOT USE) =============
// These functions have been removed to prevent creating orphaned members
// Always use the CreateUserWithProfileDialog component for creating new users

// Add subscription to existing user (for admin use)
export const addExistingMemberSubscription = async (memberData: Omit<Member, 'id'> & { id: string }): Promise<Member | null> => {
  try {
    console.log('Adding subscription for existing member:', memberData);
    
    const memberForInsert = mapMemberToSupabase(memberData);
    
    const { data, error } = await supabase
      .from('club_members')
      .insert([memberForInsert])
      .select()
      .single();
    
    if (error) {
      console.error('Error adding member subscription:', error);
      throw error;
    }
    
    console.log('Member subscription added successfully:', data);
    return mapSupabaseToMember(data);
  } catch (error) {
    console.error('Error in addExistingMemberSubscription:', error);
    throw error;
  }
};

export const updateMember = async (member: Member): Promise<Member> => {
  try {
    console.log('updateMember called with member:', { id: member.id, name: member.name, email: member.email });
    
    if (!member.id) {
      throw new Error('ID du membre manquant pour la mise à jour');
    }
    
    // Vérifier d'abord si le membre existe
    const { data: existingMember, error: checkError } = await supabase
      .from('club_members')
      .select('id, name, email')
      .eq('id', member.id)
      .maybeSingle();
    
    if (checkError) {
      console.error('Error checking existing member:', checkError);
      throw new Error(`Erreur lors de la vérification du membre: ${checkError.message}`);
    }
    
    if (!existingMember) {
      console.error('Member not found with ID:', member.id);
      throw new Error(`Aucun membre trouvé avec l'ID: ${member.id}`);
    }
    
    console.log('Existing member found:', existingMember);
    
    const memberToUpdate = mapMemberToSupabase(member);
    console.log('Mapped member data for update:', memberToUpdate);
    
    const { data, error } = await supabase
      .from('club_members')
      .update(memberToUpdate)
      .eq('id', member.id)
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('Update error:', error);
      throw new Error(`Erreur lors de la mise à jour: ${error.message}`);
    }
    
    if (!data) {
      console.error('No data returned after update for member ID:', member.id);
      throw new Error(`Aucune donnée retournée après mise à jour du membre ${member.id}`);
    }
    
    console.log('Member updated successfully:', data);
    return mapSupabaseToMember(data);
  } catch (error) {
    console.error('Error updating member:', error);
    throw error;
  }
};

export const deleteMember = async (memberId: string): Promise<void> => {
  try {
    // 1. Récupérer le user_id et email avant suppression
    const { data: member, error: fetchError } = await supabase
      .from('club_members')
      .select('user_id, email, name')
      .eq('id', memberId)
      .maybeSingle();
    
    if (fetchError) {
      console.error('Error fetching member for deletion:', fetchError);
      throw fetchError;
    }
    
    console.log('Deleting member:', { memberId, email: member?.email, hasUserId: !!member?.user_id });

    // 2. Supprimer de club_members (RLS autorise maintenant les admins)
    const { error: memberError } = await supabase
      .from('club_members')
      .delete()
      .eq('id', memberId);
    
    if (memberError) {
      console.error('Error deleting from club_members:', memberError);
      throw memberError;
    }

    // 3. Si un user_id existe, supprimer l'utilisateur auth via edge function
    if (member?.user_id) {
      try {
        const { data, error: authError } = await supabase.functions.invoke('delete-user-admin', {
          body: { userId: member.user_id }
        });
        
        if (authError) {
          console.error('Error calling delete-user-admin:', authError);
          // Ne pas throw ici, le membre est déjà supprimé de club_members
        } else if (!data?.success) {
          console.warn('delete-user-admin returned non-success:', data);
        } else {
          console.log('Successfully deleted user from auth:', member.user_id);
        }
      } catch (authError) {
        console.error('Exception calling delete-user-admin:', authError);
        // Ne pas throw, continuer même si la suppression auth échoue
      }
    }

    console.log('Member deletion completed:', { memberId, email: member?.email });
  } catch (error) {
    console.error('Error deleting member:', error);
    throw error;
  }
};

export const resetMemberPassword = async (userId: string, newPassword: string): Promise<void> => {
  try {
    const { data, error } = await supabase.functions.invoke('reset-user-password', {
      body: {
        user_id: userId,
        new_password: newPassword
      }
    });

    if (error) {
      console.error('Error resetting password:', error);
      throw new Error(`Erreur lors de la réinitialisation: ${error.message}`);
    }

    if (!data?.success) {
      throw new Error('La réinitialisation du mot de passe a échoué');
    }
  } catch (error) {
    console.error('Error in resetMemberPassword:', error);
    throw error;
  }
};

// ============= MEMBER REQUEST CONVERSION =============

export const convertRequestToMember = async (requestId: string): Promise<Member | null> => {
  try {
    // Fetch the request details
    const { data: request, error: requestError } = await supabase
      .from('member_requests')
      .select('*')
      .eq('id', requestId)
      .single();
    
    if (requestError) throw requestError;
    
    // Generate a temporary password (user will reset it via email)
    const tempPassword = Math.random().toString(36).slice(-12) + 'Aa1!';
    
    // Create the Supabase Auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: request.email,
      password: tempPassword,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: {
          firstname: request.firstname || request.name.split(' ')[0],
          lastname: request.lastname || request.name.split(' ').slice(1).join(' '),
        }
      }
    });

    if (authError) {
      console.error('Error creating auth user:', authError);
      throw new Error(`Erreur lors de la création du compte: ${authError.message}`);
    }

    if (!authData.user) {
      throw new Error('Impossible de créer le compte utilisateur');
    }

    // Create the club_members entry
    const memberData = {
      user_id: authData.user.id,
      email: request.email,
      name: request.name,
      firstname: request.firstname || request.name.split(' ')[0],
      lastname: request.lastname || request.name.split(' ').slice(1).join(' '),
      company: request.company || '',
      role: request.role || '',
      status: 'actif',
      subscription_type: 'platinum', // First 100 members get platinum
      type: 'membre',
      is_visible_in_directory: true,
      join_date: new Date().toISOString().split('T')[0],
      last_payment: new Date().toISOString().split('T')[0],
    };

    const { data: member, error: memberError } = await supabase
      .from('club_members')
      .insert([memberData])
      .select()
      .single();

    if (memberError) {
      console.error('Error creating member:', memberError);
      throw new Error(`Erreur lors de la création du profil: ${memberError.message}`);
    }

    // Update the request status to approved
    await supabase
      .from('member_requests')
      .update({ status: 'approved' })
      .eq('id', requestId);

    // Send password reset email to the user
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(request.email, {
      redirectTo: `${window.location.origin}/auth`,
    });

    if (resetError) {
      console.error('Error sending password reset email:', resetError);
      // Don't throw here, account is created, just log the error
    }

    // Send personalized welcome email via SMTP
    try {
      await supabase.functions.invoke('email-waitlist-approved', {
        body: {
          to: request.email,
          name: request.name,
          firstname: request.firstname || request.name.split(' ')[0],
          resetLink: `${window.location.origin}/auth`,
        }
      });
    } catch (emailError) {
      console.error('Error sending welcome email:', emailError);
    }

    console.log('Member created successfully from request:', member);
    return mapSupabaseToMember(member);
  } catch (error) {
    console.error('Error converting request to member:', error);
    throw error;
  }
};

// ============= ANALYTICS =============

export const getMembershipAnalytics = async () => {
  try {
    const { data: membersData, error: membersError } = await supabase
      .from('club_members')
      .select('status, subscription_type, join_date, last_payment')
      .not('user_id', 'is', null);
    
    if (membersError) throw membersError;

    // Get current date for comparisons
    const currentDate = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(currentDate.getDate() - 30);
    
    // Calculate metrics
    const total = membersData.length;
    const active = membersData.filter(m => m.status === 'active').length;
    const inactive = membersData.filter(m => m.status === 'inactive').length;
    
    // Calculate subscription types with proper counting
    const byType = membersData.reduce((acc, m) => {
      if (m.subscription_type) {
        acc[m.subscription_type] = (acc[m.subscription_type] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
    
    // Calculate actual revenue based on active members and their subscription types
    const pricing = {
      'Standard': 350,
      'Premium': 650,
      'VIP': 1000
    };
    
    const monthlyRevenue = membersData.reduce((sum, m) => {
      if (m.status === 'active' && m.subscription_type) {
        const price = pricing[m.subscription_type as keyof typeof pricing] || 0;
        return sum + price;
      }
      return sum;
    }, 0);

    // Calculate new members in the last 30 days
    const recentMembers = membersData.filter(m => {
      if (!m.join_date) return false;
      const join_date = new Date(m.join_date);
      return join_date >= thirtyDaysAgo;
    }).length;
    
    return {
      totalMembers: total,
      activeMembers: active,
      inactiveMembers: inactive,
      bySubscriptionType: byType,
      totalRevenue: monthlyRevenue,
      newMembersLastMonth: recentMembers
    };
  } catch (error) {
    console.error('Error getting membership analytics:', error);
    return {
      totalMembers: 0,
      activeMembers: 0,
      inactiveMembers: 0,
      bySubscriptionType: {},
      totalRevenue: 0,
      newMembersLastMonth: 0
    };
  }
};

// ============= BULK OPERATIONS =============

export const bulkUpdateMembers = async (memberIds: string[], updates: Partial<Member>): Promise<void> => {
  try {
    console.log('Bulk updating members:', memberIds.length, 'updates:', updates);
    
    const supabaseUpdates = mapMemberToSupabase(updates as Member);
    
    const { error } = await supabase
      .from('club_members')
      .update(supabaseUpdates)
      .in('id', memberIds);
    
    if (error) {
      console.error('Error bulk updating members:', error);
      throw new Error(`Erreur lors de la mise à jour en masse: ${error.message}`);
    }
    
    console.log('Bulk update successful for', memberIds.length, 'members');
  } catch (error) {
    console.error('Error in bulkUpdateMembers:', error);
    throw error;
  }
};

export const bulkDeleteMembers = async (memberIds: string[]): Promise<void> => {
  try {
    console.log('Bulk deleting members:', memberIds.length);
    
    const { error } = await supabase
      .from('club_members')
      .delete()
      .in('id', memberIds);
    
    if (error) {
      console.error('Error bulk deleting members:', error);
      throw new Error(`Erreur lors de la suppression en masse: ${error.message}`);
    }
    
    console.log('Bulk deletion successful for', memberIds.length, 'members');
  } catch (error) {
    console.error('Error in bulkDeleteMembers:', error);
    throw error;
  }
};